/**
 * Welke beheer-items staan in de nav, gegeven de rechten van de gebruiker (#277, UI8).
 *
 * Apart van `AppShell` omdat dit een **autorisatie-beslissing** is en geen opmaak: hier
 * hoort een test op te staan die niet de halve shell hoeft te mocken. De afbeelding
 * route→atoom is dezelfde als die van de route-guards in `router/guards.tsx`; loopt die
 * uit de pas, dan verbergt de nav iets dat wél te openen is (of omgekeerd).
 *
 * Vóór de SPA-sweep hing dit blok aan `roles.includes('tenant-admin')`. De API was sinds
 * Inc 2 rechten-gedreven, het menu niet — dus een custom rol met `user.view` kwam de
 * console binnen en zag een lege nav. `admin_console.enter` poort het blok als geheel
 * (RG22); het atoom per item bepaalt wat erin staat.
 */
import type { ReactNode } from 'react';
import {
  Article,
  Buildings,
  ClipboardText,
  Notepad,
  EnvelopeSimple,
  Exam,
  GraduationCap,
  IdentificationCard,
  Images,
  MapPin,
  PaperPlaneTilt,
  SealCheck,
  ShieldCheck,
  TreeStructure,
  Users,
  UsersThree,
} from '@phosphor-icons/react';

export interface AdminNavLink {
  path: string;
  label: string;
  icon: ReactNode;
  /**
   * Het atoom dat dit item ontsluit — of een **lijst** als het item twee pagina's heeft met elk een eigen
   * atoom (Kwalificaties: de definities en wie wat houdt): elk van die atomen laat het item zien, en
   * de route-guard (`RequireAdminConsole`) neemt dezelfde lijst.
   */
  permission: string | readonly string[];
  /**
   * De inklapbare groep waar dit item onder valt (issue 357). Het id is Engels en
   * taal-onafhankelijk, net als een route-segment; het zichtbare label komt uit
   * `ADMIN_NAV_GROUPS` via i18n.
   *
   * De indeling hieronder is een **vertrekpunt en geen vastgestelde structuur** —
   * `beheer-navigatie.md` is een voorstel en PO/PM mogen de labels en de verdeling
   * aanscherpen. Dat is hier met opzet een gegeven en geen code: verschuiven is
   * één regel wijzigen, geen verbouwing.
   */
  group?: AdminNavGroupId;
}

export type AdminNavGroupId = 'users-org' | 'learning' | 'resources' | 'messaging';

/**
 * De groepen in rendervolgorde, met hun i18n-sleutel.
 *
 * De indeling en de labels liggen bij PO/PM — issue 710. Wat hier nu staat is een experiment (feedback 22-10):
 * de groep *Instellingen* is weg, want naast een pagina *Instellingen* las die als een fout. Wat erin stond
 * (Teksten, Workflows, Goedkeuringsflows, LTI-koppelingen) is nu een tegel op die pagina — zie
 * `settingsPages.tsx`. Nieuw is *Middelen*: trainingslocaties, externe personen en bedrijven, en media.
 */
export const ADMIN_NAV_GROUPS: ReadonlyArray<{ id: AdminNavGroupId; labelKey: string; defaultOpen?: boolean }> = [
  { id: 'users-org', labelKey: 'nav.groupUsersOrg' },
  { id: 'learning', labelKey: 'nav.groupLearning' },
  { id: 'resources', labelKey: 'nav.groupResources' },
  { id: 'messaging', labelKey: 'nav.groupMessaging' },
];

/** Het recht dat de beheer-console als geheel ontsluit (RG22). */
export const ADMIN_CONSOLE_ATOM = 'admin_console.enter';

/**
 * De beheer-items in nav-volgorde, elk met het atoom dat hem ontsluit.
 *
 * `t` komt als parameter binnen in plaats van via de hook, zodat deze module puur blijft
 * en een test hem kan aanroepen zonder i18n te initialiseren.
 */
export function adminNavSpecs(t: (key: string) => string): AdminNavLink[] {
  return [
    { path: '/users', label: t('nav.users'), icon: <Users size={18} weight="regular" />, permission: 'user.view', group: 'users-org' },
    { path: '/org-units', label: t('nav.orgUnits'), icon: <TreeStructure size={18} weight="regular" />, permission: 'org_unit.view', group: 'users-org' },
    // Hoort onder *Gebruikers & organisatie* (RB12), maar de Sidebar is nog een platte
    // lijst — dus direct achter Organisatie-eenheden, waar de groep gaat ontstaan.
    { path: '/roles', label: t('nav.roles'), icon: <ShieldCheck size={18} weight="regular" />, permission: 'role.view', group: 'users-org' },
    // Naast Rollen, want het is dezelfde soort vraag: rollen zeggen wie iets mag
    // DOEN, toegangsgroepen wie iets mag ZIEN. Wie het een zoekt, zoekt in de
    // praktijk in dezelfde hoek als het ander.
    { path: '/access-groups', label: t('nav.accessGroups'), icon: <UsersThree size={18} weight="regular" />, permission: 'access_group.view', group: 'users-org' },
    // Sinds issue 1266 wijst dit naar de activiteiten-lijst zelf en niet meer naar `/catalog`.
    // Dat adres is van de lerende bladerkant (en straks van de catalogus-pagina's); een
    // beheer-menu-item dat afhankelijk van het oppervlak iets anders opent, is precies hoe
    // dezelfde link twee dingen ging betekenen.
    { path: '/activity', label: t('nav.catalog'), icon: <GraduationCap size={18} weight="regular" />, permission: 'activity.view', group: 'learning' },
    // De module vragenlijsten: vragen, categorieën en formulieren, als tabs onder één item.
    { path: '/questionnaires', label: t('nav.questionnaires'), icon: <Notepad size={18} weight="regular" />, permission: 'questionnaire.view', group: 'learning' },
    // De toetsen: dezelfde module op een eigen oppervlak, met eigen rechten. `test.view` en
    // `questionnaire.view` zijn los te delegeren: wie het ene heeft ziet niet het andere.
    { path: '/tests', label: t('nav.tests'), icon: <Exam size={18} weight="regular" />, permission: 'test.view', group: 'learning' },
    // Eén item, twee pagina's met elk een eigen atoom (kwalificaties.md §14.1): wie alleen `held_qualification.view`
    // heeft komt via dit item op *wie wat houdt* uit.
    { path: '/qualifications', label: t('nav.qualifications'), icon: <SealCheck size={18} weight="regular" />, permission: ['qualification.view', 'held_qualification.view'], group: 'learning' },
    // De inrichting van de aanvraag externe opleiding (blok 25): de soorten met hun versies. Dit is de
    // beheerkant; de medewerker krijgt pas een ingang als er een live versie is.
    { path: '/external-request-types', label: t('nav.externalRequestTypes'), icon: <ClipboardText size={18} weight="regular" />, permission: 'external_request_type.view', group: 'learning' },
    // Middelen (feedback 21, experiment): wat je bij het plannen van een training inzet, los van het leeraanbod
    // zelf. Na Leeraanbod, want de groep staat in het menu op de plek van zijn eerste item.
    { path: '/locations', label: t('nav.locations'), icon: <MapPin size={18} weight="regular" />, permission: 'location.manage', group: 'resources' },
    { path: '/external-people', label: t('nav.externalPeople'), icon: <IdentificationCard size={18} weight="regular" />, permission: 'external_person.manage', group: 'resources' },
    { path: '/external-companies', label: t('nav.externalCompanies'), icon: <Buildings size={18} weight="regular" />, permission: 'external_company.manage', group: 'resources' },
    // De mediabibliotheek (issue 1237): eigen recht, want verwijderen raakt elke activiteit die het beeld gebruikt.
    { path: '/media', label: t('nav.media'), icon: <Images size={18} weight="regular" />, permission: 'asset.manage', group: 'resources' },
    { path: '/messages', label: t('nav.sendMessage'), icon: <PaperPlaneTilt size={18} weight="regular" />, permission: 'broadcast.send', group: 'messaging' },
    { path: '/mail-templates', label: t('nav.mailTemplates'), icon: <Article size={18} weight="regular" />, permission: 'mail_template.view', group: 'messaging' },
    { path: '/mail-outbox', label: t('nav.mailOutbox'), icon: <EnvelopeSimple size={18} weight="regular" />, permission: 'mail_outbox.view', group: 'messaging' },
  ];
}

/**
 * De beheer-items die deze gebruiker mag zien. Zonder `admin_console.enter` is dat er
 * géén — dan hoort de console-nav niet half te verschijnen.
 */
export function adminNavLinks(t: (key: string) => string, held: readonly string[]): AdminNavLink[] {
  if (!held.includes(ADMIN_CONSOLE_ATOM)) {
    return [];
  }

  return adminNavSpecs(t).filter((link) =>
    (typeof link.permission === 'string' ? [link.permission] : link.permission).some((atom) => held.includes(atom)),
  );
}
